/**
 * Ranked client-side matching for in-memory lists.
 *
 * This is the command palette's scorer, generalised. It was the only decent
 * matcher in the console and it was reachable from exactly one screen; every
 * other search re-implemented `list.filter(x => x.name.includes(query))`, which
 * has no notion of a better or worse hit — typing "auto" returned matches in
 * whatever order the list happened to be sorted in.
 *
 * Dependency-free on purpose: the lists this serves (projects, commands) are a
 * few dozen entries held in memory, so a straightforward scorer beats pulling in
 * a fuzzy-search library. The query is split on whitespace and **every** term
 * must match, which is what makes incremental typing like `fleet serv` narrow
 * the way people expect instead of widening.
 */

/** Characters that start a new "word" for the word-boundary bonus. */
const WORD_BOUNDARY = /[\s\-_/.:&]/;

/**
 * Score a term that appears *literally* in the haystack: prefix > word-start >
 * substring. Returns `-1` when the term is not contained at all.
 *
 * Split out from {@link scoreTerm} because the subsequence fallback below is
 * only safe on short, specific fields. Over a long joined haystack almost any
 * word can be found as a scattered subsequence — "alpine" hides in
 * "Cortex Analytics nginx …" — so callers matching against concatenated text
 * want this half on its own.
 */
export function scoreSubstring(term: string, target: string): number {
  const index = target.indexOf(term);

  if (index === 0) return 120;
  if (index > 0) return WORD_BOUNDARY.test(target[index - 1]) ? 100 : 70;

  return -1;
}

/**
 * Score a single query term against one lowercased haystack.
 *
 * Ranked best-first: prefix > word-start > substring > subsequence. Returns a
 * negative number when the term is absent.
 */
export function scoreTerm(term: string, target: string): number {
  const literal = scoreSubstring(term, target);
  if (literal >= 0) return literal;

  // Subsequence fallback: `srvcfg` should still find "Server Configs". Reward
  // consecutive hits, penalise the gaps skipped to reach each character.
  let cursor = 0;
  let streak = 0;
  let score = 0;

  for (const character of term) {
    const found = target.indexOf(character, cursor);
    if (found === -1) return -1;

    streak = found === cursor ? streak + 1 : 0;
    score += 4 + streak * 2 - Math.min(found - cursor, 4);
    cursor = found + 1;
  }

  return score;
}

/**
 * Score one item's text fields against a query.
 *
 * `fields[0]` is the primary field and counts double, so a project whose *name*
 * contains the term outranks one that merely mentions it in its description.
 * Returns `0` for an empty query, a positive number for a match, `-1` when any
 * term is missing entirely.
 */
export function scoreFields(query: string, fields: string[]): number {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return 0;

  const primary = (fields[0] ?? '').toLowerCase();
  const haystack = fields.join(' ').toLowerCase();

  let total = 0;

  for (const term of terms) {
    const best = Math.max(
      scoreTerm(term, primary) * 2,
      scoreTerm(term, haystack),
    );
    if (best < 0) return -1;
    total += best;
  }

  return total;
}

/**
 * Filter and rank a list, best match first.
 *
 * An empty query returns the list untouched — the caller's own sort order is
 * the right answer when nobody is searching, and re-sorting it would quietly
 * override the sort control on the projects page.
 */
export function rankMatches<T>(
  items: T[],
  query: string,
  fieldsOf: (item: T) => string[],
): T[] {
  if (!query.trim()) {
    return items;
  }

  return items
    .map((item) => ({ item, score: scoreFields(query, fieldsOf(item)) }))
    .filter((entry) => entry.score >= 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.item);
}
